import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import useCountUp from '../hooks/useCountUp';

const FEATURES = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
      </svg>
    ),
    title: 'Demo Portfolio',
    desc: '$10,000 in virtual funds to practice with. Track your P&L, holdings, and trade history.',
    img: '/app/monthly_pnl.png',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
      </svg>
    ),
    title: 'Real-Time Charts',
    desc: 'Professional candlestick charts powered by TradingView with multiple timeframes and indicators.',
    img: '/app/realtime_chart.png',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
    title: 'Multiple Indicators',
    desc: 'SMA, EMA, RSI, and Bollinger Bands overlay directly on the chart for analysis.',
    img: '/app/indicators.png',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
      </svg>
    ),
    title: 'Instant Execution',
    desc: 'Market and limit orders executed instantly against live Binance price feeds.',
    img: '/app/open_position.png',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'SL/TP Protection',
    desc: 'Set stop-loss and take-profit levels on every position. Auto-trigger at your target price.',
    img: '/app/measurement.png',
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
      </svg>
    ),
    title: 'Drawing Tools',
    desc: 'Trendlines, horizontal lines, and measurement tools for technical analysis on the chart.',
    img: '/app/trendline.png',
  },
];

const FAQS = [
  { q: 'Is this real money?', a: 'No. CryptoTerminal is a demo trading platform. All trades use virtual funds ($10,000 starting balance). No real cryptocurrency or fiat currency is involved.' },
  { q: 'Where does the price data come from?', a: 'Live price data is streamed directly from Binance via their public WebSocket API. You see the same prices as real traders, in real-time.' },
  { q: 'Do I need to create an account?', a: 'Yes. You need a free account to access the trading terminal. Sign up with your email — no credit card or KYC required.' },
  { q: 'Can I reset my demo balance?', a: 'Yes. Click the "Reset Demo Account" option in your profile dropdown to reset your portfolio back to $10,000 and clear all trade history.' },
  { q: 'What trading features are available?', a: 'Market orders, limit orders, stop-loss, take-profit, technical indicators (SMA, EMA, RSI, Bollinger Bands), drawing tools, multiple chart types, and real-time P&L tracking.' },
  { q: 'Is there a mobile app?', a: 'Not yet. CryptoTerminal is a web app optimized for desktop and tablet. A phone notice will appear if your screen is too small.' },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count: balance, ref: balanceRef } = useCountUp(10);
  const { count: coins, ref: coinsRef } = useCountUp(50);
  const { count: free, ref: freeRef } = useCountUp(100);
  const { count: hours, ref: hoursRef } = useCountUp(24);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
        } else {
          entry.target.classList.remove('is-visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <div className="landing-logo">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="6" fill="#FECC58"/>
              <path d="M8 18L12 10L16 15L20 8" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>CryptoTerminal</span>
          </div>
          <button className="landing-hamburger" onClick={() => setMenuOpen(!menuOpen)} aria-label="Menu">
            {menuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            )}
          </button>
          <div className={`landing-nav-links ${menuOpen ? 'open' : ''}`}>
            <a href="#demo" onClick={() => setMenuOpen(false)}>Demo</a>
            <a href="#features" onClick={() => setMenuOpen(false)}>Features</a>
            <a href="#how-it-works" onClick={() => setMenuOpen(false)}>How It Works</a>
            <a href="#faq" onClick={() => setMenuOpen(false)}>FAQ</a>
            <Link to="/login" className="landing-nav-link" onClick={() => setMenuOpen(false)}>Log In</Link>
            <Link to="/signup" className="landing-btn-nav" onClick={() => setMenuOpen(false)}>Get Started</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hex-bg" />
        <div className="landing-hero-content">
          <div className="landing-badge">DEMO TRADING PLATFORM</div>
          <h1>
            Trade Crypto, <span className="landing-gold">Risk Free.</span>
          </h1>
          <p className="landing-hero-sub">
            Practice trading with $10,000 in demo funds. Real-time Binance data,
            professional charting tools, and instant order execution — no wallet required.
          </p>
          <div className="landing-hero-stats">
            <div className="landing-stat" ref={balanceRef}>
              <span className="landing-stat-val">${balance}K</span>
              <span className="landing-stat-label">Demo Balance</span>
            </div>
            <div className="landing-stat-divider" />
            <div className="landing-stat" ref={coinsRef}>
              <span className="landing-stat-val">{coins}+</span>
              <span className="landing-stat-label">Coins Available</span>
            </div>
            <div className="landing-stat-divider" />
            <div className="landing-stat" ref={freeRef}>
              <span className="landing-stat-val">{free}%</span>
              <span className="landing-stat-label">Free to Use</span>
            </div>
            <div className="landing-stat-divider" />
            <div className="landing-stat" ref={hoursRef}>
              <span className="landing-stat-val">{hours}/7</span>
              <span className="landing-stat-label">Live Data</span>
            </div>
          </div>
          <div className="landing-marquee-wrap">
            <div className="landing-marquee">
              {[
                { sym: 'BTC', img: '/crypto-svg/bitcoin-logo.svg' },
                { sym: 'ETH', img: '/crypto-svg/ethereum-logo.svg' },
                { sym: 'BNB', img: '/crypto-svg/bnb-coin-logo.svg' },
                { sym: 'SOL', img: '/crypto-svg/solanaLogo.svg' },
                { sym: 'XRP', img: '/crypto-svg/xrp-coin-logo.svg' },
                { sym: 'DOGE', img: '/crypto-svg/doge-coin-logo.svg' },
                { sym: 'USDT', img: '/crypto-svg/tether-usdt-logo.svg' },
                { sym: 'TRX', img: '/crypto-svg/tron-trx-coin-logo.svg' },
                { sym: 'LINK', img: '/crypto-svg/chainlink-logo.svg' },
                { sym: 'XMR', img: '/crypto-svg/monero-xmr-logo.svg' },
              ].map((c, i) => (
                <div className="landing-marquee-item" key={i}>
                  <img src={c.img} alt={c.sym} className="landing-marquee-icon" />
                  <span>{c.sym}</span>
                </div>
              ))}
              {[
                { sym: 'BTC', img: '/crypto-svg/bitcoin-logo.svg' },
                { sym: 'ETH', img: '/crypto-svg/ethereum-logo.svg' },
                { sym: 'BNB', img: '/crypto-svg/bnb-coin-logo.svg' },
                { sym: 'SOL', img: '/crypto-svg/solanaLogo.svg' },
                { sym: 'XRP', img: '/crypto-svg/xrp-coin-logo.svg' },
                { sym: 'DOGE', img: '/crypto-svg/doge-coin-logo.svg' },
                { sym: 'USDT', img: '/crypto-svg/tether-usdt-logo.svg' },
                { sym: 'TRX', img: '/crypto-svg/tron-trx-coin-logo.svg' },
                { sym: 'LINK', img: '/crypto-svg/chainlink-logo.svg' },
                { sym: 'XMR', img: '/crypto-svg/monero-xmr-logo.svg' },
              ].map((c, i) => (
                <div className="landing-marquee-item" key={`dup-${i}`}>
                  <img src={c.img} alt={c.sym} className="landing-marquee-icon" />
                  <span>{c.sym}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Demo Video */}
      <section className="landing-demo" id="demo">
        <div className="landing-section-inner reveal-on-scroll">
          <div className="landing-section-badge">LIVE DEMO</div>
          <h2>See the <span className="landing-gold">Terminal In Action</span></h2>
          <div className="landing-demo-video-wrap reveal-on-scroll">
            <video
              src="/app/crypto_terminal_demo.mp4"
              autoPlay
              loop
              muted
              playsInline
              className="landing-demo-video"
            />
            <div className="landing-demo-video-overlay">
              <div className="landing-demo-video-badge">INTERACTIVE TERMINAL</div>
              <p>Real-time charts, instant order execution, portfolio tracking — all in your browser.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="landing-features" id="features">
        <div className="landing-section-inner reveal-on-scroll">
          <div className="landing-section-badge">FEATURES</div>
          <h2>Everything You Need to <span className="landing-gold">Master Trading</span></h2>
          <div className="landing-features-grid">
            {FEATURES.map((f, i) => (
              <div className="landing-feature-card reveal-on-scroll" key={i} style={{ '--stagger': i }}>
                <div className="landing-feature-img-wrap">
                  <img src={f.img} alt={f.title} />
                </div>
                <div className="landing-feature-body">
                  <div className="landing-feature-icon">{f.icon}</div>
                  <h3>{f.title}</h3>
                  <p>{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="landing-how" id="how-it-works">
        <div className="landing-section-inner reveal-on-scroll">
          <div className="landing-section-badge">HOW IT WORKS</div>
          <h2>Start Trading in <span className="landing-gold">3 Steps</span></h2>
          <div className="landing-steps">
            <div className="landing-step reveal-on-scroll" style={{ '--stagger': 0 }}>
              <div className="landing-step-num">01</div>
              <h3>Create Account</h3>
              <p>Sign up with your email. No credit card, no KYC — just instant access.</p>
            </div>
            <div className="landing-step-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="landing-step reveal-on-scroll" style={{ '--stagger': 1 }}>
              <div className="landing-step-num">02</div>
              <h3>Fund Your Demo</h3>
              <p>Your account starts with $10,000 in virtual funds ready to trade.</p>
            </div>
            <div className="landing-step-arrow">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#FECC58" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/>
              </svg>
            </div>
            <div className="landing-step reveal-on-scroll" style={{ '--stagger': 2 }}>
              <div className="landing-step-num">03</div>
              <h3>Start Trading</h3>
              <p>Pick a coin, analyze the chart, and place your first trade in seconds.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-faq" id="faq">
        <div className="landing-section-inner reveal-on-scroll">
          <div className="landing-section-badge">FAQ</div>
          <h2><span className="landing-gold">Frequently Asked Questions</span></h2>
          <div className="landing-faq-list">
            {FAQS.map((faq, i) => (
              <div className={`landing-faq-item reveal-on-scroll ${openFaq === i ? 'open' : ''}`} key={i} style={{ '--stagger': i }}>
                <button className="landing-faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <span>{faq.q}</span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points={openFaq === i ? "18 15 12 9 6 15" : "6 9 12 15 18 9"}/>
                  </svg>
                </button>
                <div className="landing-faq-answer">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta">
        <div className="landing-section-inner reveal-on-scroll">
          <h2>Ready to Start <span className="landing-gold">Trading?</span></h2>
          <p>Join now and get $10,000 in demo funds to practice risk-free.</p>
          <Link to="/signup" className="landing-btn-primary landing-btn-lg">Create Free Account</Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-left">
            <div className="landing-logo">
              <svg width="24" height="24" viewBox="0 0 28 28" fill="none">
                <rect width="28" height="28" rx="6" fill="#FECC58"/>
                <path d="M8 18L12 10L16 15L20 8" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>CryptoTerminal</span>
            </div>
            <p className="landing-footer-tagline">Professional crypto trading simulation platform.</p>
          </div>
          <div className="landing-footer-socials">
            <a href="https://www.linkedin.com/in/avinash-kumar-20946b21b/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
              </svg>
            </a>
            <a href="https://www.instagram.com/avinash_vermaa/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.88 0 1.441 1.441 0 012.88 0z"/>
              </svg>
            </a>
            <a href="mailto:code6969nation@gmail.com" aria-label="Email">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
              </svg>
            </a>
          </div>
        </div>
        <div className="landing-footer-divider" />
        <div className="landing-footer-copy">
          <span>&copy; {new Date().getFullYear()} 
            <span className="landing-gold">  CryptoTerminal</span>
            {' '}
            by 
            <span className="landing-gold">  Avinash</span>.
          </span>
          <span className="live-dot"></span>
          {' '}
          <span>Demo only. No real funds involved. Built for learning.</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
